/**
 * InfinitSales - inboxStore (frente C2, flag `inbox_store`)
 *
 * Estado da LISTA de conversas por canal, fora da arvore do React. Os shells
 * dos 4 inboxes desmontam ao trocar de rota; sem isto, cada volta comecava com
 * `chats=[]` e skeleton ate a primeira pagina chegar (1,4 a 7,5 s no QR e no
 * Business, medido em 22/09). Aqui a lista sobrevive a troca de inbox e a
 * proxima montagem pinta o cache na hora e revalida por baixo (stale-while-
 * revalidate: revalida quando `fetchedAt` tem mais de 15 s).
 *
 * Por canal: { chats, page, hasMore, filters, status, fetchedAt, scrollTop }.
 *
 *  - chats: a pagina carregada (mesmo shape que o shell do canal usa).
 *  - page: quantas paginas de PAGE_SIZE ja entraram (1 = so a primeira).
 *  - hasMore: ha mais paginas no servidor.
 *  - filters: { labelIds, extra } do ultimo fetch. Lista filtrada NAO e "as
 *    conversas mais recentes"; a montagem seguinte descarta o cache quando o
 *    ultimo fetch tinha filtro (a UI de filtro da ChatList nasce limpa).
 *  - status: ultimo /status do canal (o prefetch grava; o shell pode ler).
 *  - fetchedAt: epoch ms do ultimo fetch completo (0 = nunca).
 *  - scrollTop: posicao da lista virtualizada ao sair do canal.
 *
 * Sem lib nova: subscribe/getSnapshot + useSyncExternalStore do React 18.
 * Limpo no logout (authService.logout chama clearInboxStore).
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { getBootFlag } from '../../hooks/useFeatureFlags';

export const INBOX_CHANNELS = ['wa_personal', 'wa_business', 'instagram', 'messenger', 'widget'];
export const INBOX_STALE_MS = 15_000;

function emptySlice() {
  return {
    chats: [],
    page: 0,
    hasMore: false,
    filters: null,
    status: null,
    fetchedAt: 0,
    scrollTop: 0,
  };
}

const _slices = {};
const _listeners = {};
// Canais com shell montado agora: o listener global de SSE do layout so marca
// como velho o cache de quem NAO esta montado (o shell montado ja aplica o
// patch pelos proprios handlers).
const _mounted = {};
INBOX_CHANNELS.forEach((c) => {
  _slices[c] = emptySlice();
  _listeners[c] = new Set();
  _mounted[c] = 0;
});

function assertChannel(channel) {
  if (!_slices[channel]) throw new Error(`inboxStore: canal desconhecido "${channel}"`);
}

function emit(channel) {
  _listeners[channel].forEach((fn) => {
    try { fn(); } catch { /* listener nao derruba os demais */ }
  });
}

export function getInboxSlice(channel) {
  assertChannel(channel);
  return _slices[channel];
}

export function subscribeInbox(channel, fn) {
  assertChannel(channel);
  _listeners[channel].add(fn);
  return () => { _listeners[channel].delete(fn); };
}

/** Patch parcial do slice (objeto ou updater). */
export function setInboxSlice(channel, patch) {
  assertChannel(channel);
  const prev = _slices[channel];
  const next = typeof patch === 'function' ? patch(prev) : patch;
  if (!next || next === prev) return;
  _slices[channel] = { ...prev, ...next };
  emit(channel);
}

/** Troca a lista (array ou updater no padrao do setState). */
export function setInboxChats(channel, updater) {
  setInboxSlice(channel, (prev) => {
    const chats = typeof updater === 'function' ? updater(prev.chats) : updater;
    if (chats === prev.chats) return null;
    return { chats: Array.isArray(chats) ? chats : [] };
  });
}

/** Aplica um patch em toda conversa que casar (id ou predicado). */
export function patchInboxChat(channel, match, patch) {
  const casa = typeof match === 'function' ? match : (c) => c && c.id === match;
  setInboxChats(channel, (prev) => {
    let tocou = false;
    const next = prev.map((c) => {
      if (!casa(c)) return c;
      tocou = true;
      return { ...c, ...(typeof patch === 'function' ? patch(c) : patch) };
    });
    return tocou ? next : prev;
  });
}

/** Insere no topo ou substitui a conversa que casar. */
export function upsertInboxChat(channel, chat, match) {
  const casa = typeof match === 'function' ? match : (c) => c && chat && c.id === chat.id;
  setInboxChats(channel, (prev) => {
    const idx = prev.findIndex(casa);
    if (idx === -1) return [chat, ...prev];
    const next = [...prev];
    next[idx] = { ...prev[idx], ...chat };
    return next;
  });
}

export function removeInboxChat(channel, match) {
  const casa = typeof match === 'function' ? match : (c) => c && c.id === match;
  setInboxChats(channel, (prev) => {
    const next = prev.filter((c) => !casa(c));
    return next.length === prev.length ? prev : next;
  });
}

/**
 * Posicao da lista sem notificar assinantes: e lida so na PROXIMA montagem
 * (initialScrollOffset), e emitir a cada tick de scroll re-renderizaria o
 * shell inteiro. Muta o slice atual de proposito.
 */
export function setInboxScrollTopSilent(channel, scrollTop) {
  if (!_slices[channel]) return;
  _slices[channel].scrollTop = Number(scrollTop) || 0;
}

/** Marca o cache do canal como velho: a proxima montagem revalida. */
export function markInboxStale(channel) {
  if (!_slices[channel]) return;
  if (_slices[channel].fetchedAt === 0) return;
  setInboxSlice(channel, { fetchedAt: 1 });
}

export function isInboxStale(channel, ttl = INBOX_STALE_MS) {
  const s = getInboxSlice(channel);
  return s.fetchedAt === 0 || Date.now() - s.fetchedAt > ttl;
}

export function isInboxChannelMounted(channel) {
  return (_mounted[channel] || 0) > 0;
}

/** Ha filtro server-side gravado no slice? (lista filtrada nao vira cache). */
export function inboxFiltersActive(filters) {
  if (!filters) return false;
  const { labelIds, extra } = filters;
  if (Array.isArray(labelIds) && labelIds.length > 0) return true;
  if (!extra) return false;
  return Boolean(
    extra.member_id || extra.since || extra.created_from || extra.created_to
    || extra.unanswered
    || (Array.isArray(extra.custom_fields) && extra.custom_fields.length > 0)
    || (Array.isArray(extra.label_ids) && extra.label_ids.length > 0),
  );
}

/** Logout / troca de conta: nada da sessao anterior sobrevive. */
export function clearInboxStore() {
  INBOX_CHANNELS.forEach((c) => {
    _slices[c] = emptySlice();
    emit(c);
  });
}

/**
 * Hook do store (sempre assina; quem decide pela flag e useChannelChats).
 * Devolve o slice e os setters estaveis do canal.
 */
export function useInboxChats(channel) {
  assertChannel(channel);
  const subscribe = useCallback((fn) => subscribeInbox(channel, fn), [channel]);
  const getSnapshot = useCallback(() => getInboxSlice(channel), [channel]);
  const slice = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const setChats = useCallback((u) => setInboxChats(channel, u), [channel]);
  const setSlice = useCallback((p) => setInboxSlice(channel, p), [channel]);
  const patchChat = useCallback((m, p) => patchInboxChat(channel, m, p), [channel]);
  const upsertChat = useCallback((c, m) => upsertInboxChat(channel, c, m), [channel]);
  const removeChat = useCallback((m) => removeInboxChat(channel, m), [channel]);

  return useMemo(() => ({
    ...slice,
    setChats,
    setSlice,
    patchChat,
    upsertChat,
    removeChat,
  }), [slice, setChats, setSlice, patchChat, upsertChat, removeChat]);
}

/**
 * Decide pela flag `inbox_store` UMA vez, na montagem do shell (a flag chega
 * por fetch; trocar de fonte no meio da vida do shell misturaria as duas).
 *
 * Com a flag OFF devolve o useState de hoje (semeado por `initial`), byte a
 * byte. Com a flag ON devolve a fatia do store, com `cacheUsable` dizendo se
 * a lista guardada serve para a primeira pintura (ja houve fetch e ele nao
 * tinha filtro) e `stale` se precisa revalidar.
 *
 * `initial` e a semente do modo OFF (o cache de modulo dos shells de IG e MS).
 */
export function useChannelChats(channel, initial = {}) {
  const useStoreRef = useRef(null);
  // Flag de BOOT (override, cache carregado ou copia persistida da ultima
  // resposta): com o boot otimista o shell monta antes do fetch das flags.
  if (useStoreRef.current === null) useStoreRef.current = getBootFlag('inbox_store');
  const fromStore = useStoreRef.current;

  // Os dois caminhos sao chamados sempre (regra dos hooks); so um e devolvido.
  const store = useInboxChats(channel);
  // Contador de shells montados por canal (para o listener global de SSE).
  useEffect(() => {
    if (!fromStore) return undefined;
    _mounted[channel] = (_mounted[channel] || 0) + 1;
    return () => {
      _mounted[channel] = Math.max(0, (_mounted[channel] || 1) - 1);
    };
  }, [channel, fromStore]);

  const local = useLocalChats(initial);

  // Setters ESTAVEIS (bound ao canal): entram em deps de useCallback nos
  // shells sem recriar loadChats a cada mudanca da lista.
  const noop = useCallback(() => {}, []);
  const setHasMoreStore = useCallback((v) => setInboxSlice(channel, { hasMore: Boolean(v) }), [channel]);
  const setStatusStore = useCallback((v) => setInboxSlice(channel, { status: v }), [channel]);
  // Chamado ao fim de cada fetch completo da primeira pagina (ou apos
  // "carregar mais", com page incrementada).
  const markFetched = useCallback(({ page = 1, hasMore, filters } = {}) => setInboxSlice(channel, {
    fetchedAt: Date.now(),
    page,
    ...(hasMore === undefined ? {} : { hasMore: Boolean(hasMore) }),
    ...(filters === undefined ? {} : { filters }),
  }), [channel]);
  const setFilters = useCallback((filters) => setInboxSlice(channel, { filters }), [channel]);
  const setScrollTop = useCallback(
    (scrollTop) => setInboxSlice(channel, { scrollTop: Number(scrollTop) || 0 }),
    [channel],
  );

  return useMemo(() => {
    if (!fromStore) {
      return {
        fromStore: false,
        chats: local.chats,
        setChats: local.setChats,
        hasMore: local.hasMore,
        setHasMore: local.setHasMore,
        status: local.status,
        setStatus: local.setStatus,
        cacheUsable: false,
        stale: true,
        fetchedAt: 0,
        page: 0,
        scrollTop: 0,
        markFetched: noop,
        setFilters: noop,
        setScrollTop: noop,
        patchChat: null,
        upsertChat: null,
        removeChat: null,
      };
    }
    const cacheUsable = store.fetchedAt > 0 && !inboxFiltersActive(store.filters);
    return {
      fromStore: true,
      chats: store.chats,
      setChats: store.setChats,
      hasMore: store.hasMore,
      setHasMore: setHasMoreStore,
      status: store.status,
      setStatus: setStatusStore,
      cacheUsable,
      stale: !cacheUsable || Date.now() - store.fetchedAt > INBOX_STALE_MS,
      fetchedAt: store.fetchedAt,
      page: store.page,
      scrollTop: store.scrollTop,
      markFetched,
      setFilters,
      setScrollTop,
      patchChat: store.patchChat,
      upsertChat: store.upsertChat,
      removeChat: store.removeChat,
    };
  }, [fromStore, store, local, noop, setHasMoreStore, setStatusStore, markFetched, setFilters, setScrollTop]);
}

// --- internos ---------------------------------------------------------------

// useState de hoje, embrulhado para o useChannelChats devolver um objeto
// estavel. Fica num hook separado so para manter a ordem dos hooks fixa.
function useLocalChats(initial) {
  const [chats, setChats] = useState(() => initial.chats || []);
  const [hasMore, setHasMore] = useState(() => Boolean(initial.hasMore));
  const [status, setStatus] = useState(() => initial.status ?? null);
  return useMemo(
    () => ({ chats, setChats, hasMore, setHasMore, status, setStatus }),
    [chats, hasMore, status],
  );
}

const inboxStore = {
  getInboxSlice,
  subscribeInbox,
  setInboxSlice,
  setInboxChats,
  patchInboxChat,
  upsertInboxChat,
  removeInboxChat,
  markInboxStale,
  isInboxStale,
  isInboxChannelMounted,
  clearInboxStore,
};

export default inboxStore;
